import * as T from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { wood, cream, dark, trayLining, lacquer, floral, shell } from './materials.js';

// Industrial-design model (tools/build-model.mjs), metres. World units: 1 mm = 0.03, shell bottom at y = 0.
const MM = .03, BASE = 24.1;
const W = (x, y, z) => new T.Vector3(x * MM, (y + BASE) * MM, z * MM);
export const ROWS = ['boom','clap','shake','tok'];
// Pad colours from the 2026-09-17 renders (top to bottom); also the pad glow colour.
export const COLORS = [0xf0a478,0xd3de9e,0xa9d5e6,0xf5d47c];
const FLOOR_Y = (-5.8 + BASE) * MM, FIELD_TOP = (-.3 + BASE) * MM, TOP = (.9 + BASE) * MM;
export const LIFT_Y = TOP + .42;
let HOLES = null;
export const gridPoint = (row,step) => { const [x,z] = HOLES[row*8+step]; return new T.Vector3(x*1000*MM, FLOOR_Y, z*1000*MM); };
export const trayPoint = id => new T.Vector3(-4.95+((id-1)%12)*.91,.13,4.72+Math.floor((id-1)/12)*1.04);

const HIT = new T.MeshBasicMaterial({visible:false});
const plain = (color,roughness=.6) => new T.MeshStandardMaterial({color,roughness});
function mesh(geo,mat,parent,x=0,y=0,z=0){
  const m=new T.Mesh(geo,mat);m.position.set(x,y,z);m.castShadow=m.receiveShadow=true;
  if(!mat.visible){m.layers.set(1);m.castShadow=m.receiveShadow=false;}
  parent.add(m);return m;
}
function box(parent,w,h,d,x,y,z,mat,r=.09){return mesh(new RoundedBoxGeometry(w,h,d,4,Math.min(r,h*.48,w*.48,d*.48)),mat,parent,x,y,z);}
function cyl(parent,r,h,x,y,z,mat,rt=r){return mesh(new T.CylinderGeometry(rt,r,h,32),mat,parent,x,y,z);}
function ring(parent,r,x,y,z,mat,tube=.02){const m=mesh(new T.TorusGeometry(r,tube,8,40),mat,parent,x,y,z);m.rotation.x=-Math.PI/2;m.castShadow=false;return m;}
function label(parent,text,x,y,z,width=.8,color='#646749'){
  const canvas=document.createElement('canvas'),icon=text.length<=2,height=icon?512:128;canvas.width=512;canvas.height=height;
  const c=canvas.getContext('2d');c.fillStyle=color;c.textAlign='center';c.textBaseline='middle';c.font=icon?'500 330px sans-serif':'600 66px sans-serif';c.fillText(text,256,height/2);
  const tex=new T.CanvasTexture(canvas);tex.colorSpace=T.SRGBColorSpace;tex.anisotropy=8;
  const m=mesh(new T.PlaneGeometry(width,width*height/512),new T.MeshStandardMaterial({map:tex,transparent:true,depthWrite:false,roughness:.8,polygonOffset:true,polygonOffsetFactor:-2}),parent,x,y,z);m.rotation.x=-Math.PI/2;m.castShadow=false;m.userData.aoIgnore=true;
  let current=text;m.userData.setText=value=>{if(value===current)return;current=value;c.clearRect(0,0,512,height);c.fillText(value,256,height/2);tex.needsUpdate=true;};return m;
}
// Printed accompaniment icons: tree, leaf, water, flower (as on the key caps in the renders).
function icon(parent,type,y,size){
  const c=document.createElement('canvas');c.width=c.height=256;const ctx=c.getContext('2d');
  ctx.fillStyle=type===3?'#e39a45':'#6f8f55';ctx.strokeStyle=ctx.fillStyle;ctx.lineWidth=23;ctx.lineCap='round';
  if(type===0){ctx.beginPath();ctx.moveTo(128,26);ctx.lineTo(182,94);ctx.lineTo(159,94);ctx.lineTo(201,150);ctx.lineTo(153,150);ctx.lineTo(153,210);ctx.lineTo(108,210);ctx.lineTo(108,150);ctx.lineTo(61,150);ctx.lineTo(99,94);ctx.lineTo(77,94);ctx.closePath();ctx.fill();}
  else if(type===1){ctx.beginPath();ctx.moveTo(57,189);ctx.bezierCurveTo(40,70,140,83,199,47);ctx.bezierCurveTo(205,171,143,209,69,178);ctx.fill();ctx.strokeStyle='#f3efe2';ctx.lineWidth=5;ctx.beginPath();ctx.moveTo(59,199);ctx.quadraticCurveTo(112,135,173,98);ctx.stroke();}
  else if(type===2){for(const yy of[94,160]){ctx.beginPath();ctx.moveTo(58,yy);ctx.bezierCurveTo(100,yy-53,135,yy+50,197,yy-9);ctx.stroke();}}
  else{for(let i=0;i<5;i++){const a=i*6.28/5;ctx.beginPath();ctx.arc(128+Math.sin(a)*49,128+Math.cos(a)*49,35,0,6.28);ctx.fill();}ctx.fillStyle='#fdf6e2';ctx.beginPath();ctx.arc(128,128,23,0,6.28);ctx.fill();}
  const map=new T.CanvasTexture(c);map.colorSpace=T.SRGBColorSpace;map.anisotropy=8;
  const m=mesh(new T.PlaneGeometry(size,size),new T.MeshStandardMaterial({map,transparent:true,roughness:.7,depthWrite:false,polygonOffset:true,polygonOffsetFactor:-2}),parent,0,y,0);m.rotation.x=-Math.PI/2;m.castShadow=false;m.userData.aoIgnore=true;
}
function silhouette(parent,x,z,height,type,rotation=0){
  const group=new T.Group();group.position.set(x,(-2.7+BASE)*MM,z);group.rotation.y=rotation;group.userData.static=true;parent.add(group);
  const s=new T.Shape();
  if(type==='fir'){[[-.10,0],[-.10,.18],[-.43,.18],[-.27,.47],[-.38,.47],[-.21,.75],[-.30,.75],[0,1.25],[.30,.75],[.21,.75],[.38,.47],[.27,.47],[.43,.18],[.10,.18],[.10,0]].forEach(([x,y],i)=>i?s.lineTo(x,y):s.moveTo(x,y));s.closePath();}
  else{s.moveTo(-.43,0);s.lineTo(-.43,.25);s.bezierCurveTo(-.7,.6,-.35,.98,-.12,.81);s.bezierCurveTo(-.03,1.22,.50,1.07,.38,.70);s.bezierCurveTo(.78,.6,.63,.20,.48,0);s.closePath();}
  mesh(new T.ExtrudeGeometry(s,{depth:.055,bevelEnabled:true,bevelSize:.014,bevelThickness:.014,bevelSegments:2,curveSegments:24}),wood,group);
  const front=new T.Mesh(new T.ShapeGeometry(s,32),lacquer(type==='fir'?0x55704a:0x8fa561,.72));front.position.z=.071;group.add(front);
  group.scale.setScalar(height/1.25);return group;
}
function roundedShape(x,z,w,d,r){const s=new T.Shape();s.moveTo(x+r,z);s.lineTo(x+w-r,z);s.quadraticCurveTo(x+w,z,x+w,z+r);s.lineTo(x+w,z+d-r);s.quadraticCurveTo(x+w,z+d,x+w-r,z+d);s.lineTo(x+r,z+d);s.quadraticCurveTo(x,z+d,x,z+d-r);s.lineTo(x,z+r);s.quadraticCurveTo(x,z,x+r,z);return s;}
function extrude(parent,s,h,y,mat,bevel=.06){
  const g=new T.ExtrudeGeometry(s,{depth:h,bevelEnabled:bevel>0,bevelSegments:bevel>.05?4:2,steps:1,bevelSize:bevel,bevelThickness:bevel,curveSegments:32});g.rotateX(Math.PI/2);
  planarUV(g,6);return mesh(g,mat,parent,0,y,0);
}
// Wood grain and the floral print need UVs; the CAD export has none worth keeping.
function planarUV(g,scale=1.2){
  const p=g.attributes.position,n=g.attributes.normal,uv=new Float32Array(p.count*2);
  for(let i=0;i<p.count;i++){
    const up=Math.abs(n.getY(i))>.7;
    uv[i*2]=(up?p.getX(i):p.getX(i)+p.getZ(i))/scale;uv[i*2+1]=(up?p.getZ(i):p.getY(i)*1.6)/scale;
  }
  g.setAttribute('uv',new T.BufferAttribute(uv,2));return g;
}

// ---- load the model: bake quantized parts to plain float geometry in world units ----
async function loadParts(url){
  const gltf=await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(url);
  gltf.scene.updateMatrixWorld(true);
  const toWorld=new T.Matrix4().makeTranslation(0,BASE*MM,0).multiply(new T.Matrix4().makeScale(1000*MM,1000*MM,1000*MM));
  const byName=new Map();
  gltf.scene.traverse(o=>{
    if(!o.isMesh)return;
    const src=o.geometry,g=new T.BufferGeometry();
    for(const name of['position','normal']){
      const a=src.attributes[name],out=new Float32Array(a.count*3);
      for(let i=0;i<a.count;i++){out[i*3]=a.getX(i);out[i*3+1]=a.getY(i);out[i*3+2]=a.getZ(i);}
      g.setAttribute(name,new T.BufferAttribute(out,3));
    }
    g.setIndex(src.index.clone());
    g.applyMatrix4(new T.Matrix4().multiplyMatrices(toWorld,o.matrixWorld));
    const name=o.name.replace(/_\d+$/,'')||o.parent.name;
    if(!byName.has(name))byName.set(name,[]);byName.get(name).push(g);
  });
  const parts=new Map();for(const [k,list] of byName)parts.set(k,list.length>1?mergeGeometries(list):list[0]);
  return {parts,holes:gltf.scene.userData.holes};
}
// A part that moves: centre its geometry so it can press, slide or turn in place.
function movable(geo,mat,parent){
  geo.computeBoundingBox();const c=geo.boundingBox.getCenter(new T.Vector3());c.y=geo.boundingBox.min.y;
  geo.translate(-c.x,-c.y,-c.z);const m=mesh(geo,mat,parent,c.x,c.y,c.z);m.userData.rest=c.clone();return m;
}

// Piece templates: parts relative to the hole they sit in, so every clone seats on a hole floor.
const TEMPLATES={};
function makeTemplates(parts){
  const bark=wood;
  const spec={
    round:[['round_base',bark],['round_stem',bark],['round_crown',lacquer(0x8e9e5a,.36)]],
    egg:[['egg_base',bark],['egg_stem',bark],['egg_crown',lacquer(0x9eab66,.36)]],
    pineL:[['pineL_base',bark],['pineL_trunk',bark],['pineL_leaf',lacquer(0x68825b,.36)]],
    pineS:[['pineS_base',bark],['pineS_trunk',bark],['pineS_leaf',lacquer(0x7d9765,.36)]],
    bush:[['bush_base',bark],['bush_leaf',lacquer(0xabbd6f,.36)]],
    flower:[['flower_base',bark],['flower_petal',lacquer(0xf1c24f,.36)],['flower_inner',lacquer(0xf6d879,.36)],['flower_center',cream]],
    mush:[['mush_base',bark],['mush_stem',bark],['mush_cap',lacquer(0xd27a3e,.36)]]
  };
  for(const [kind,list] of Object.entries(spec)){
    const base=parts.get(list[0][0]);base.computeBoundingBox();const c=base.boundingBox.getCenter(new T.Vector3());
    let best=0,d=Infinity;for(let i=0;i<32;i++){const p=gridPoint(i>>3,i&7),dd=Math.hypot(p.x-c.x,p.z-c.z);if(dd<d){d=dd;best=i;}}
    const origin=gridPoint(best>>3,best&7);
    TEMPLATES[kind]=list.map(([name,mat])=>{const g=parts.get(name);g.translate(-origin.x,-origin.y,-origin.z);parts.delete(name);return [g,mat];});
    const bb=new T.Box3();for(const [g] of TEMPLATES[kind]){g.computeBoundingBox();bb.union(g.boundingBox);}
    TEMPLATES[kind].height=bb.max.y;
  }
}
// Rows: round/egg tree, pine, bush, flower/mushroom. Variant changes shape only, never the sound.
export function plant(kind,variant=0){
  const name=[variant%2?'egg':'round',variant%2?'pineS':'pineL','bush',variant%2?'mush':'flower'][kind];
  const g=new T.Group();
  for(const [geo,mat] of TEMPLATES[name])mesh(geo,mat,g);
  // Tapered pick proxy: tall trees must not swallow clicks meant for short pieces behind them.
  g.userData.hit=cyl(g,.33,TEMPLATES[name].height+.05,0,TEMPLATES[name].height/2,0,HIT,TEMPLATES[name].height>.9?.16:.3);
  return g;
}

export async function createModel(scene,url){
  const {parts,holes}=await loadParts(url);
  if(!holes||holes.length!==32)throw new Error('Model is missing hole positions');
  HOLES=holes;makeTemplates(parts);
  const root=new T.Group();scene.add(root);
  const picks=[],holeList=[],pieces=new Map(),leds=[],pads=[],worldKeys=[],worldLeds=[],knobs=[];
  const take=name=>{const g=parts.get(name);if(!g)throw new Error('Model part missing: '+name);parts.delete(name);return g;};
  // Static body.
  mesh(take('shell'),shell,root);
  mesh(take('panel_cream'),cream,root);
  mesh(planarUV(take('panel_field'),4.6),floral,root);
  mesh(take('panel_pond'),new T.MeshPhysicalMaterial({color:0x8fc6d6,roughness:.05,clearcoat:1,clearcoatRoughness:.04}),root);
  // Short white glints on the water, as printed in the renders.
  const glint=new T.MeshBasicMaterial({color:0xf4fbfd});
  for(const [x,z,w,a] of [[-170,-24,9,.1],[-162,-16,13,.05],[-172,-2,10,-.05],[-160,8,12,.08],[-168,20,9,0],[-157,27,7,.1]]){const g=box(root,w*MM,.006,1.3*MM,x*MM,(-3.9+BASE)*MM+.004,z*MM,glint,.018);g.rotation.y=a;g.castShadow=g.receiveShadow=false;}
  mesh(planarUV(take('hole_floor'),1.4),wood,root);
  mesh(planarUV(take('hole_wall'),1.4),wood,root);
  // Dark cavity under the speaker grille and the step-light slots.
  const cavity=new T.Mesh(new T.CircleGeometry(.72,48),dark);cavity.rotation.x=-Math.PI/2;cavity.position.copy(W(-126,-6,86));root.add(cavity);
  // House.
  const houseMat=wood.clone();houseMat.color.set(0xf7e2c2);
  mesh(planarUV(take('house_body'),1.2),houseMat,root);
  mesh(take('house_roof'),lacquer(0x6f8a4f,.66),root);
  mesh(take('house_door'),lacquer(0xc66b36,.55),root);
  mesh(take('house_knob'),cream,root);
  // Warm window: find the embossed window on the curved front wall and put a glowing pane on it.
  const windowMat=new T.MeshPhysicalMaterial({color:0xffe7b0,roughness:.3,emissive:0xffb35c,emissiveIntensity:.5});
  const probe=new T.Raycaster(),facing=new T.Vector3(.49,0,.87),target=W(-137.5,27,-63.5);
  probe.set(target.clone().addScaledVector(facing,1.5),facing.clone().negate());
  const wallHit=probe.intersectObjects(root.children,false)[0];
  const pane=mesh(new T.CircleGeometry(.24,48),windowMat,root);pane.castShadow=false;
  if(wallHit){pane.position.copy(wallHit.point).addScaledVector(wallHit.face.normal,.012);pane.lookAt(pane.position.clone().add(wallHit.face.normal));}
  else{pane.position.copy(target);pane.lookAt(target.clone().add(facing));}
  const windowLight=new T.PointLight(0xffbb66,.8,2.4,2);windowLight.position.copy(pane.position).addScaledVector(facing,.5);root.add(windowLight);
  // Flat plywood trees behind the house, as in the renders.
  silhouette(root,-5.35,-3.35,1.5,'fir',.35);silhouette(root,-4.55,-3.62,1.95,'fir',.1);silhouette(root,-3.75,-3.5,1.45,'leaf',-.05);silhouette(root,-3.1,-3.28,1.35,'fir',-.2);silhouette(root,-5.55,-2.25,1.1,'fir',.6);
  // Holes: pick proxy, card target ring and hover ring.
  for(let row=0;row<4;row++)for(let step=0;step<8;step++){
    const p=gridPoint(row,step);
    const hit=cyl(root,.32,.06,p.x,FIELD_TOP,p.z,HIT);hit.userData={kind:'hole',row,step};picks.push(hit);
    const target=ring(root,.33,p.x,FIELD_TOP+.02,p.z,lacquer([0x7d8f45,0x587148,0x9bb05a,0xf1c24f][row],.7),.022);target.visible=false;
    const hover=ring(root,.345,p.x,FIELD_TOP+.04,p.z,new T.MeshBasicMaterial({color:0xf3d27b}),.02);hover.visible=false;
    target.userData=hover.userData={kind:'hole',row,step};holeList.push({row,step,target,hover,hit});
  }
  // Drum pads, top to bottom.
  for(let i=0;i<4;i++){const pad=movable(take('pad'+i),new T.MeshPhysicalMaterial({color:COLORS[i],roughness:.3,clearcoat:.9,clearcoatRoughness:.12,sheen:.3,sheenColor:0xffffff}),root);pad.userData.kind='pad';pad.userData.row=i;pad.userData.travel=.05;picks.push(pad);pads.push(pad);}
  // Step lights sit in the eight slots under the columns.
  for(let step=0;step<8;step++){
    const x=gridPoint(0,step).x;
    const led=box(root,.44,.05,.13,x,(-4.9+BASE)*MM,50.7*MM,new T.MeshPhysicalMaterial({color:0x9a9a8a,roughness:.3,clearcoat:.4,emissive:0xffc263,emissiveIntensity:0}),.03);led.castShadow=false;leds.push(led);
  }
  const play=movable(take('play'),lacquer(0x809b62,.42),root);play.userData={...play.userData,kind:'play',travel:.06};picks.push(play);
  const playTop=play.geometry.boundingBox.max.y-play.geometry.boundingBox.min.y;
  const playRing=ring(root,.47,play.position.x,play.position.y+.02,play.position.z,new T.MeshStandardMaterial({color:0x495438,roughness:.65,emissive:0xffd28b,emissiveIntensity:0}),.014);
  const playMark=label(play,'',0,playTop+.004,0,.26,'#e9ecd9');
  // Knobs: volume (left) and tempo (right). Notch points back at mid-range; dots mark the travel.
  for(const [i,name] of [[0,'knob_volume'],[1,'knob_tempo']]){
    const knob=movable(take(name),wood,root);knob.userData.kind=i?'tempo':'volume';picks.push(knob);knobs.push(knob);
    for(let n=0;n<11;n++){const a=-Math.PI*.76+n*Math.PI*1.52/10;cyl(root,.016,.01,knob.position.x+Math.sin(a)*.43,(-5.1+BASE)*MM+.006,knob.position.z-Math.cos(a)*.43,lacquer(0x98a17b,.7));}
  }
  // Accompaniment keys (left to right) with their indicator lights.
  const WK=['pop','africa','asian','latin'];
  for(let i=0;i<4;i++){
    const key=movable(take('bgmkey'+i),cream,root);key.userData={...key.userData,kind:'world',world:WK[i],travel:.05};picks.push(key);worldKeys.push(key);
    icon(key,i,key.geometry.boundingBox.max.y+.003,.3);
    const led=movable(take('bgmled'+i),new T.MeshPhysicalMaterial({color:0xb3b9a0,roughness:.4,emissive:0x8aa769,emissiveIntensity:0}),root);led.castShadow=false;worldLeds.push(led);
  }
  const power=movable(take('power'),lacquer(0x7c8f5a,.5),root);power.userData.kind='power';picks.push(power);
  // Rhythm cards: the printed field sheet, lifted onto the panel when inserted.
  // The field's flat top face (holes included) is the rhythm-card outline.
  const flat=take('card_sheet');{const q=flat.attributes.position;for(let i=0;i<q.count;i++)q.setY(i,0);flat.computeVertexNormals();flat.computeBoundingBox();}
  const fc=flat.boundingBox.getCenter(new T.Vector3());
  const card=mesh(flat,plain(0xe8d8a6,.9),root,0,FIELD_TOP+.012,0);card.visible=false;card.userData={kind:'card'};card.castShadow=false;picks.push(card);
  const tabPos=W(174,0,-72);
  const tab=box(root,.5,.04,.27,tabPos.x,FIELD_TOP+.03,tabPos.z,cream,.06);tab.userData={kind:'card'};picks.push(tab);tab.visible=false;
  const tabLabel=label(root,'A',tabPos.x,FIELD_TOP+.055,tabPos.z,.23);tabLabel.visible=false;
  // Loose-piece tray.
  const trayShape=roundedShape(-5.66,4.12,11.44,2.12,.23);trayShape.holes.push(new T.Path(roundedShape(-5.50,4.27,11.12,1.82,.14).getPoints(40)));
  extrude(root,trayShape,.14,.18,wood,.025);box(root,11.16,.04,1.87,.06,.10,5.18,trayLining,.08);
  const trayHit=box(root,11.5,.06,2.25,.06,.17,5.18,HIT,.02);trayHit.userData={kind:'tray'};picks.push(trayHit);
  const cardStack=[];
  const centred=flat.clone();centred.translate(-fc.x,0,-fc.z);
  window.KidstepEngine.cards.forEach((definition,i)=>{
    const group=new T.Group();group.position.set(9.0,.03+i*.03,.2);group.rotation.y=Math.PI/2;root.add(group);group.userData={kind:'cardStack',id:definition.id};cardStack.push(group);
    const material=plain(window.KidstepEngine.worlds[definition.world].card,.94);
    const face=mesh(centred,material,group);face.castShadow=i===0;
    const tabX=-2.6+i*1.75;box(group,.72,.02,.34,tabX,0,2.62,material,.025);label(group,definition.cn,tabX,.016,2.66,.59,'#626348');
    for(let row=0;row<4;row++)for(const step of definition.faces.A.pat[ROWS[row]]){const p=gridPoint(row,step);ring(group,.33,p.x-fc.x,.012,p.z-fc.z,lacquer([0x7d8f45,0x587148,0x9bb05a,0xf1c24f][row],.8),.02);}
    {const a=gridPoint(0,7);label(group,'A',a.x+.6-fc.x,.016,a.z-fc.z,.25,'#626348');}
    box(group,8.4,.06,5.6,0,0,.1,HIT,.02);
    mergeStatic(group,new Set());
  });
  // Press/slide/turn helpers used by the interaction layer.
  const press=(o,down)=>{o.position.y=o.userData.rest.y-(down?o.userData.travel||.05:0);};
  // The lever travels the full 20 mm slot: up (away from the player) is on.
  const setPower=on=>{power.position.z=power.userData.rest.z+(on?-5.6:4.6)*MM;};
  const setKnob=(i,f)=>{knobs[i].rotation.y=Math.PI*.75-T.MathUtils.clamp(f,0,1)*Math.PI*1.5;};
  mergeStatic(root,new Set([...picks,...leds,...worldLeds,playRing,pane,cavity,...holeList.flatMap(h=>[h.target,h.hover])]));
  return {root,picks,holes:holeList,pieces,cardStack,leds,pads,worldKeys,worldLeds,card,tab,tabLabel,playRing,play,playMark,knobs,power,windowMat,windowLight,press,setPower,setKnob,lift:LIFT_Y,ringY:FIELD_TOP+.02,center:new T.Vector3(1.4,.5,1.0)};
}

// Fold fixed parts that share a material into one draw call. Interactive and animated parts stay separate.
function mergeStatic(root,keep){
  root.updateMatrixWorld(true);
  const toRoot=root.matrixWorld.clone().invert(),buckets=new Map();
  root.traverse(o=>{
    if(!o.isMesh||keep.has(o)||!o.visible||!o.material.visible||o.material.transparent||o.layers.mask!==1)return;
    if(o.userData.kind&&o.userData.kind!=='hole')return;
    for(let a=o.parent;a!==root;a=a.parent)if(!a||!a.userData.static)return;
    const key=o.material.uuid+'|'+o.castShadow+'|'+o.receiveShadow;
    if(!buckets.has(key))buckets.set(key,[]);buckets.get(key).push(o);
  });
  for(const list of buckets.values()){
    if(list.length<2)continue;
    const geos=list.map(o=>{
      let g=o.geometry.clone().applyMatrix4(toRoot.clone().multiply(o.matrixWorld));
      if(!g.index)g.setIndex([...Array(g.attributes.position.count).keys()]);
      for(const name of Object.keys(g.attributes))if(!['position','normal','uv'].includes(name))g.deleteAttribute(name);
      if(!g.attributes.uv)planarUV(g);
      g.clearGroups();return g;
    });
    const merged=mergeGeometries(geos,false);if(!merged)continue;
    const m=new T.Mesh(merged,list[0].material);m.castShadow=list[0].castShadow;m.receiveShadow=list[0].receiveShadow;root.add(m);
    for(const o of list){o.removeFromParent();}
    geos.forEach(g=>g.dispose());
  }
}
